'use client'
import { cubicBezier } from 'motion'
import { motion } from 'motion/react'
import type { FC } from 'react'
import type { Content } from '@/content-types'
import { asText } from '@/lib/content-fields'
import { ContentLink, RichText, type SliceComponentProps } from '@/lib/rich-text'
import { linkResolver } from '@/utils/linkResolver'

/**
 * Props for `StepsSlice`.
 */
export type StepsSliceProps = SliceComponentProps<Content.StepsSliceSlice>

/**
 * Component for "StepsSlice" Slices.
 */
const StepsSlice: FC<StepsSliceProps> = ({ slice }) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="container flex flex-col items-center justify-center gap-3 py-20 md:py-28"
    >
      <RichText
        field={slice.primary.title}
        components={{
          heading2: ({ children }) => <h2 className="display-title w-full text-center">{children}</h2>,
        }}
      />
      <div className="mt-10 flex w-full justify-center pl-4 lg:mt-[91px] lg:pl-0">
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{
            ease: cubicBezier(0.3, 0.3, 0.3, 0.4),
            duration: 0.6,
          }}
          className="flex flex-col items-start gap-y-7 lg:flex-row lg:gap-x-7"
        >
          {slice.primary.steps.map((step, index) => (
            <div key={asText(step.step_title)} className="flex flex-row items-start gap-x-7">
              <span className="bg-ink flex size-11 shrink-0 items-center justify-center rounded-full text-white">
                <span className="font-rubik text-base font-medium tracking-tight text-white lg:text-2xl">
                  {index + 1}
                </span>
              </span>
              <div className="flex flex-col lg:w-[310px]">
                <RichText
                  field={step.step_title}
                  components={{
                    heading5: ({ children }) => (
                      <h3 className="font-rubik text-foreground text-[22px] font-semibold tracking-tight">
                        {children}
                      </h3>
                    ),
                  }}
                />
                <RichText
                  field={step.description}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-foreground mt-2 text-base font-light tracking-tight opacity-90 lg:text-[17px]">
                        {children}
                      </p>
                    ),
                  }}
                />
              </div>
              {index !== slice.primary.steps.length - 1 && (
                <div className="mx-8 hidden h-[148px] w-[2px] shrink-0 bg-[#1818181A] lg:block" />
              )}
            </div>
          ))}
        </motion.div>
      </div>
      <ContentLink
        field={slice.primary.button}
        linkResolver={linkResolver}
        className="bg-ink hover:bg-primary mt-16 mb-2 inline-block rounded-full px-10 py-3 text-center leading-7 font-semibold text-white transition-all duration-200 ease-in-out md:text-lg"
      >
        {slice.primary.button.text}
      </ContentLink>
    </section>
  )
}

export default StepsSlice
